import React, {useContext, useState, useEffect} from 'react'
import {useIntl} from 'react-intl'
import {ReactCountryFlag} from 'react-country-flag'
import { Formik, FieldArray, Field, Form } from 'formik';
import {useListView} from '@/components/table/core/ListViewProvider'
import {useQueryResponse} from '@/components/table/core/QueryResponseProvider'
import {CustomerContext} from '../../CustomerContext'
import {MissingCreditTrx} from '../../../../statement/core/_models'
import {getMissingCredits, reconcileMissingCredits} from '../../../../statement/core/_requests'
import {InputText, FormActions} from '@/components/form'

const MissingCreditForm = () => {
  const intl = useIntl()
  const { customer, refresh: refreshUser  } = useContext(CustomerContext)
  const {setItemForUpdate, setAction} = useListView()
  const [missingCredit, setMissingCredit] = useState<Array<MissingCreditTrx>>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const {refetch} = useQueryResponse()
  const close = () => {
    setItemForUpdate(undefined)
    setAction(undefined)
  }

  useEffect(() => {
    const load = async () => {
      try {
        setMissingCredit(await getMissingCredits(customer?._id))
      } catch (e: unknown) {
        console.log(e)
      } finally {
        setIsLoading(false)
      }
    }
    void(customer && isLoading && load())
  }, [isLoading, customer])

  if (!customer) {
    return (<></>)
  }

  if (missingCredit.length === 0) {
    return (
      <>No missing credit</>
    )
  }

  return (
    <Formik
      initialValues={{trxs: missingCredit}}
      onSubmit={async (values) => {
        try {
          await reconcileMissingCredits(customer._id, values.trxs)
          await refreshUser()
          refetch()
          close()
        } catch (e: unknown) {
          console.log(e)
        }
      }}
    >
      {({ values }) => {
        return (
          <Form> 
            <div className="flex-grow-1">
              <FieldArray name="trxs">
                {() => (
                  <table className="table align-middle table-row-dashed">
                    <thead>
                      <tr className="border-bottom fs-6 fw-bold text-muted">
                        <th className="min-w-175px pb-2">Date</th>
                        <th className="w-125px text-start pb-2">Paymode</th>
                        <th className="min-w-80px text-end pb-2">Code</th>
                        <th className="min-w-60px text-center pb-2">Payout</th>
                        <th className="w-125px text-end pb-2">Credits</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(values?.trxs || []).map((row, index) => (
                        <tr key={`mc-row-${index}`}>
                          <td>
                            {intl.formatDate(row.date, {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: 'numeric',
                              second: 'numeric'
                            })}
                          </td>
                          <td>
                            <div className='d-flex align-items-center'>
                              <ReactCountryFlag countryCode={row.country} style={{ fontSize: '1.5em' }} className='me-3' />
                              {intl.formatMessage({id: `P2C.PAYMODE_${row.type?.toUpperCase()}`})}
                              <span className="ms-2">
                                {intl.formatNumber(row.amount || 0, {
                                  style: 'currency',
                                  currency: row.currency,
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}
                              </span>
                            </div>
                          </td>
                          <td className="text-end">{row.code}</td>
                          <td className="text-center">{row.payout}</td>
                          <td className="text-end">
                            <Field
                              name={`trxs[${index}].credit`}
                              component={InputText}
                              className="form-control-sm"
                              type="number"
                              placeholder="0"
                              required={true}
                              rawInput={true}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </FieldArray>
            </div>
            <Field
              component={FormActions}
              position='end'
              close={close}
            />
          </Form>
        )
      }}
    </Formik>
  )
}

export default MissingCreditForm
